import Phaser from "phaser";
import { GameModel, foodRegistry } from "../core/model";
import { SegmentSkin } from "../core/tail/SegmentSkin";
import { GridLayout } from "./GridLayout";

function skinToColor(skin: SegmentSkin): number | null {
  switch (skin) {
    case SegmentSkin.Spent:
      return 0x666666;
    case SegmentSkin.Purple:
      return 0xbf5bff;
    case SegmentSkin.Star:
      return 0xffd347;
    case SegmentSkin.Icy:
      return 0x4da3ff;
    default:
      return null;
  }
}

export class GridRenderer {
  private snakeRects: Phaser.GameObjects.Rectangle[] = [];
  private enemySnakeRects: Phaser.GameObjects.Rectangle[][] = [];
  private foodRects: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private obstacleRects: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private border?: Phaser.GameObjects.Graphics;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly layout: GridLayout
  ) {}

  clearDynamicSprites(): void {
    this.snakeRects.forEach((r) => r.destroy());
    this.snakeRects = [];

    this.enemySnakeRects.forEach((arr) => arr.forEach((r) => r.destroy()));
    this.enemySnakeRects = [];

    this.foodRects.forEach((r) => r.destroy());
    this.foodRects.clear();

    this.obstacleRects.forEach((r) => r.destroy());
    this.obstacleRects.clear();

    if (this.border) {
      this.border.destroy();
      this.border = undefined;
    }
  }

  syncFromModel(model: GameModel, hard: boolean): void {
    const tileSize = this.layout.getTileSize();

    if (hard) {
      this.clearDynamicSprites();
      this.drawBorder(model); // draw perimeter once per hard sync
    } else if (!this.border) {
      // Safety: if border was somehow cleared, recreate it
      this.drawBorder(model);
    }

    // --- Player Snake ---

    if (hard || this.snakeRects.length !== model.snake.segments.length) {
      this.snakeRects.forEach((r) => r.destroy());
      this.snakeRects = model.snake.segments.map((_seg, index) => {
        const rect = this.scene.add.rectangle(
          0,
          0,
          tileSize - 2,
          tileSize - 2,
          0x00ff00
        );
        rect.setDepth(10 + (model.snake.length - index));
        return rect;
      });
    }

    const level = model.laserLevel;

    model.snake.segments.forEach((seg, index) => {
      const { wx, wy } = this.layout.gridToWorld(seg.x, seg.y);
      const rect = this.snakeRects[index];
      rect.setPosition(wx, wy);

      const t =
        this.snakeRects.length <= 1
          ? 0
          : index / (this.snakeRects.length - 1);

      let color: number;

      // Override snake segment color when it carries tail metadata (ammo/skins).
      const skin = model.tailModel.getSegment(index)?.skin ?? SegmentSkin.Normal;
      const override = skinToColor(skin);
      if (override !== null) {
        color = override;
        rect.setFillStyle(color);
        return;
      }

      if (level <= 0) {
        // green tail -> bright green head
        const g = Math.floor(255 - t * 120);
        color = (0x00 << 16) | (g << 8) | 0x00;
      } else if (level === 1) {
        // cyan -> magenta
        const r = Math.floor(255 * t);
        const g = Math.floor(255 * (1 - t) * 0.5);
        const b = 255;
        color = (r << 16) | (g << 8) | b;
      } else if (level === 2) {
        // orange -> yellow
        const r = 255;
        const g = Math.floor(120 + (1 - t) * 120);
        const b = Math.floor(30 * t);
        color = (r << 16) | (g << 8) | b;
      } else {
        // level 3: purple -> white
        const r = Math.floor(180 + (1 - t) * 75);
        const g = Math.floor(80 + (1 - t) * 175);
        const b = 255;
        color = (r << 16) | (g << 8) | b;
      }

      rect.setFillStyle(color);
    });

    // --- Enemy Snakes ---

    this.enemySnakeRects.forEach((arr) => arr.forEach((r) => r.destroy()));
    this.enemySnakeRects = [];

    for (const enemy of model.enemySnakes ?? []) {
      const rects = enemy.segments.map((_seg, index) => {
        const rect = this.scene.add.rectangle(
          0,
          0,
          tileSize - 2,
          tileSize - 2,
          0xff3333 // enemy base color
        );
        rect.setDepth(5 + (enemy.length - index));
        return rect;
      });

      enemy.segments.forEach((seg, index) => {
        const { wx, wy } = this.layout.gridToWorld(seg.x, seg.y);
        const rect = rects[index];
        rect.setPosition(wx, wy);

        const t =
          rects.length <= 1 ? 0 : index / (rects.length - 1);
        const r = 255;
        const g = Math.floor(60 + t * 60); // 60 -> 120
        const b = Math.floor(60 + t * 60); // 60 -> 120
        const color = (r << 16) | (g << 8) | b;

        rect.setFillStyle(color);
      });

      this.enemySnakeRects.push(rects);
    }

    // --- Foods ---

    const desiredFoodKeys = new Set<string>();
    for (const food of model.foods) {
      const key = `${food.x},${food.y}`;
      desiredFoodKeys.add(key);
      let rect = this.foodRects.get(key);
      const color = foodRegistry[food.id].color;
      const { wx, wy } = this.layout.gridToWorld(food.x, food.y);

      if (!rect) {
        rect = this.scene.add.rectangle(
          wx,
          wy,
          tileSize - 4,
          tileSize - 4,
          color
        );
        rect.setDepth(4);
        this.foodRects.set(key, rect);
      } else {
        rect.setPosition(wx, wy);
        rect.setFillStyle(color);
      }
    }
    for (const [key, rect] of this.foodRects.entries()) {
      if (!desiredFoodKeys.has(key)) {
        rect.destroy();
        this.foodRects.delete(key);
      }
    }

    // --- Obstacles ---

    const desiredObstacleKeys = new Set<string>();
    for (const key of model.obstacles) {
      desiredObstacleKeys.add(key);
      let rect = this.obstacleRects.get(key);
      if (!rect) {
        const [sx, sy] = key.split(",").map((n) => parseInt(n, 10));
        const { wx, wy } = this.layout.gridToWorld(sx, sy);
        rect = this.scene.add.rectangle(
          wx,
          wy,
          tileSize - 3,
          tileSize - 3,
          0x555555
        );
        rect.setDepth(3);
        this.obstacleRects.set(key, rect);
      }
    }
    for (const [key, rect] of this.obstacleRects.entries()) {
      if (!desiredObstacleKeys.has(key)) {
        rect.destroy();
        this.obstacleRects.delete(key);
      }
    }
  }

  /** Draw a white rectangle around the playable grid. */
  private drawBorder(model: GameModel): void {
    // Replace any existing border (e.g. after resize / hard sync)
    this.border?.destroy();
    this.border = undefined;

    const tileSize = this.layout.getTileSize();

    // Centers of top-left and bottom-right tiles
    const { wx: leftCenterX, wy: topCenterY } = this.layout.gridToWorld(0, 0);
    const { wx: rightCenterX, wy: bottomCenterY } = this.layout.gridToWorld(
      model.width - 1,
      model.height - 1
    );

    const left = leftCenterX - tileSize / 2;
    const top = topCenterY - tileSize / 2;
    const width = (rightCenterX - leftCenterX) + tileSize;
    const height = (bottomCenterY - topCenterY) + tileSize;

    const g = this.scene.add.graphics();
    g.lineStyle(2, 0xffffff, 1);
    g.strokeRect(left, top, width, height);
    g.setDepth(1);

    this.border = g;
  }
}
